import type { VRM } from '@pixiv/three-vrm'
import { createVRMAnimationClip, type VRMAnimation } from '@pixiv/three-vrm-animation'
import type * as THREE from 'three'
import type { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { ViewerError } from '../types'
import { resolveVrmaFilePath } from './resolveVrmaFilePath'

type GLTFWithVRMAnimations = GLTF & {
  userData: {
    vrmAnimations?: unknown[]
  }
}

export type LoadVrmaParams = {
  source: string
  vrm: VRM
  gltfLoader: GLTFLoader
  isCurrent?: () => boolean
}

export type LoadVrmaSuccess = {
  status: 'loaded'
  source: string
  clip: THREE.AnimationClip
  durationMs: number
}

export type LoadVrmaFailure = {
  status: 'failed'
  error: ViewerError
}

export type LoadVrmaResult = LoadVrmaSuccess | LoadVrmaFailure

const createMotionLoadError = (message: string): ViewerError => ({
  code: 'MOTION_LOAD_FAILED',
  message,
  recoverable: true,
})

const extractFirstVrmAnimation = (gltf: GLTFWithVRMAnimations): VRMAnimation | null => {
  const vrmAnimation = gltf.userData.vrmAnimations?.[0]
  return (vrmAnimation as VRMAnimation | undefined) ?? null
}

const removeExpressionTracks = (clip: THREE.AnimationClip, vrm: VRM): THREE.AnimationClip => {
  const expressionManager = vrm.expressionManager
  if (!expressionManager) return clip

  const expressionTrackNames = new Set(
    expressionManager.expressions
      .map(({ expressionName }) => expressionManager.getExpressionTrackName(expressionName))
      .filter((trackName): trackName is string => trackName !== null)
  )
  if (expressionTrackNames.size === 0) return clip

  clip.tracks = clip.tracks.filter((track) => !expressionTrackNames.has(track.name))
  return clip
}

/**
 * MotionItem.source の VRMA を読み込み、対象 VRM で再生できる AnimationClip に変換する。
 * 旧 VRMViewer と同じく position / translation track は保持し、VRMA 側の腰や root の移動を再生に反映する。
 */
export const loadVrma = async ({ source, vrm, gltfLoader, isCurrent }: LoadVrmaParams): Promise<LoadVrmaResult> => {
  const resolvedPath = resolveVrmaFilePath(source)
  if (!resolvedPath) {
    return {
      status: 'failed',
      error: createMotionLoadError('VRMA の source が指定されていません'),
    }
  }

  try {
    const gltf = (await gltfLoader.loadAsync(resolvedPath)) as GLTFWithVRMAnimations
    if (isCurrent && !isCurrent()) {
      return { status: 'failed', error: createMotionLoadError('読み込みをキャンセルしました') }
    }
    const vrmAnimation = extractFirstVrmAnimation(gltf)
    if (!vrmAnimation) {
      return {
        status: 'failed',
        error: createMotionLoadError('VRMA にモーションデータが含まれていません'),
      }
    }

    const clip = createVRMAnimationClip(vrmAnimation, vrm)
    if (!clip) {
      return {
        status: 'failed',
        error: createMotionLoadError('VRMA から AnimationClip を作成できませんでした'),
      }
    }

    return {
      status: 'loaded',
      source,
      clip: removeExpressionTracks(clip, vrm),
      durationMs: clip.duration * 1000,
    }
  } catch (error) {
    return {
      status: 'failed',
      error: createMotionLoadError(error instanceof Error ? error.message : String(error)),
    }
  }
}
